import type { Ref, ShallowRef } from 'vue'
import { ref, watch, onMounted, onBeforeUnmount, nextTick } from 'vue'

const NEAR_BOTTOM_THRESHOLD = 30
const SMOOTH_SCROLL_DURATION = 500

interface UseChatScrollOptions {
    scrollEl:
        | Readonly<ShallowRef<HTMLDivElement | null>>
        | Ref<HTMLDivElement | undefined>
    status: () => string | undefined
    messagesLength: () => number | undefined
    lastMessageRole: () => string | undefined
    /**
     * Element whose height tracks the content height, watched for late
     * growth (lazy tool outputs). Defaults to the scroll element's first
     * child.
     */
    contentEl?: () => HTMLElement | null
    onScrollUp?: () => void
    onScrollDown?: () => void
}

export function useChatScroll(options: UseChatScrollOptions) {
    const {
        scrollEl,
        status,
        messagesLength,
        lastMessageRole,
        contentEl,
        onScrollUp,
        onScrollDown,
    } = options

    const userScrolledUp = ref(false)
    const isAtBottom = ref(true)
    let isAutoScrolling = false
    let mutationObserver: MutationObserver | null = null
    let resizeObserver: ResizeObserver | null = null
    let initMutationObserver: MutationObserver | null = null
    let contentResizeObserver: ResizeObserver | null = null
    let lastScrollTop = 0

    // --- Core scroll ---

    const scrollToBottom = (behavior: ScrollBehavior = 'smooth') => {
        if (!scrollEl.value || userScrolledUp.value) {
            return
        }
        isAutoScrolling = true
        scrollEl.value.scrollTo({
            top: scrollEl.value.scrollHeight,
            behavior,
        })
        const unlockDelay = behavior === 'instant' ? 0 : SMOOTH_SCROLL_DURATION
        setTimeout(() => {
            isAutoScrolling = false
        }, unlockDelay)
    }

    // --- User scroll detection ---
    // Intent to scroll away comes from input events (wheel/touch): unlike
    // scroll deltas they are never produced by programmatic scrolls, layout
    // shifts or elastic overscroll bounces, so the auto-scroll never fights
    // the user nor disables itself spuriously.

    const onWheel = (event: WheelEvent) => {
        if (event.deltaY < 0) {
            userScrolledUp.value = true
        }
    }

    let lastTouchY = 0
    const onTouchStart = (event: TouchEvent) => {
        lastTouchY = event.touches[0]?.clientY ?? 0
    }
    const onTouchMove = (event: TouchEvent) => {
        const y = event.touches[0]?.clientY ?? 0
        // Finger moving down drags the content up
        if (y > lastTouchY) {
            userScrolledUp.value = true
        }
        lastTouchY = y
    }

    const handleScroll = () => {
        if (!scrollEl.value) {
            return
        }
        const {
            scrollTop: currentScrollTop,
            scrollHeight,
            clientHeight,
        } = scrollEl.value
        isAtBottom.value =
            scrollHeight - clientHeight - currentScrollTop <
            NEAR_BOTTOM_THRESHOLD
        if (isAutoScrolling) {
            lastScrollTop = currentScrollTop
            return
        }

        if (currentScrollTop < lastScrollTop) {
            onScrollUp?.()
        }

        if (currentScrollTop > lastScrollTop) {
            // Back at the bottom: re-enable auto-scroll
            if (isAtBottom.value) {
                userScrolledUp.value = false
            }
            onScrollDown?.()
        }

        lastScrollTop = currentScrollTop
    }

    // --- Streaming auto-follow ---
    // Tracks the growing content towards the bottom, never above the
    // current position. While the answer fits the space reserved by the
    // last-message spacer in PkChatbotMessages the content bottom does not
    // move, so the freshly sent user message stays anchored at the top;
    // once the answer overflows, the follow keeps the tail in view until
    // the user scrolls away (wheel/touch sets `userScrolledUp`).
    const autoFollow = () => {
        if (isAutoScrolling || userScrolledUp.value || !scrollEl.value) {
            return
        }
        const el = scrollEl.value
        const bottom = el.scrollHeight - el.clientHeight
        if (bottom <= el.scrollTop) {
            return
        }
        isAtBottom.value = true
        isAutoScrolling = true
        el.scrollTop = bottom
        requestAnimationFrame(() => {
            isAutoScrolling = false
        })
    }

    const startMutationScroll = () => {
        if (mutationObserver || !scrollEl.value) {
            return
        }
        mutationObserver = new MutationObserver(autoFollow)
        mutationObserver.observe(scrollEl.value, {
            childList: true,
            subtree: true,
            characterData: true,
        })
    }

    const stopMutationScroll = () => {
        mutationObserver?.disconnect()
        mutationObserver = null
    }

    // --- Status watcher ---

    watch(status, (newStatus, oldStatus) => {
        if (newStatus === 'streaming' || newStatus === 'submitted') {
            startMutationScroll()
            return
        }
        if (newStatus === 'ready' && oldStatus === 'streaming') {
            // Keep observer active to catch footer buttons rendering
            return
        }
        stopMutationScroll()
    })

    // --- New message watcher ---

    watch(messagesLength, async () => {
        if (!messagesLength()) {
            return
        }
        if (lastMessageRole() === 'assistant') {
            return
        }
        // A user send ends the initial-load phase: on a chat whose content
        // first overflows while the answer streams in, the initial observers
        // would otherwise slam the scroll to the bottom past the anchor
        cleanupInitialObservers()
        userScrolledUp.value = false
        isAutoScrolling = true
        await nextTick()
        scrollToBottom()
    })

    // --- Initial scroll ---

    let initialScrollTimer: ReturnType<typeof setTimeout> | null = null
    const INITIAL_SCROLL_SETTLE_MS = 300

    const tryInitialScroll = () => {
        if (!scrollEl.value) {
            return
        }
        if (scrollEl.value.scrollHeight <= scrollEl.value.clientHeight) {
            return
        }
        scrollEl.value.scrollTop = scrollEl.value.scrollHeight

        // Reset the settle timer on each mutation — disconnect only after stability
        if (initialScrollTimer) {
            clearTimeout(initialScrollTimer)
        }
        initialScrollTimer = setTimeout(
            cleanupInitialObservers,
            INITIAL_SCROLL_SETTLE_MS,
        )
    }

    const cleanupInitialObservers = () => {
        resizeObserver?.disconnect()
        resizeObserver = null
        initMutationObserver?.disconnect()
        initMutationObserver = null
        if (initialScrollTimer) {
            clearTimeout(initialScrollTimer)
            initialScrollTimer = null
        }
    }

    onMounted(() => {
        if (!scrollEl.value) {
            return
        }
        scrollEl.value.addEventListener('wheel', onWheel, { passive: true })
        scrollEl.value.addEventListener('touchstart', onTouchStart, {
            passive: true,
        })
        scrollEl.value.addEventListener('touchmove', onTouchMove, {
            passive: true,
        })

        // Mounted mid-send (e.g. fullscreen first message: the messages view
        // replaces the empty state after the message is already in) is not a
        // history load: skip the initial bottom-jump and follow the stream
        // from the anchored position instead.
        const isSendInFlight =
            status() === 'submitted' || status() === 'streaming'
        if (isSendInFlight) {
            startMutationScroll()
        } else {
            resizeObserver = new ResizeObserver(tryInitialScroll)
            resizeObserver.observe(scrollEl.value)

            initMutationObserver = new MutationObserver(tryInitialScroll)
            initMutationObserver.observe(scrollEl.value, {
                childList: true,
                subtree: true,
            })
        }

        // Late content growth: tool outputs render lazily (async chunks,
        // dynamic imports: diagrams, images...) and can change the content
        // height long after the initial scroll settled. The wrapper height
        // tracks the content height (the scroll element itself never
        // resizes with it).
        const wrapper = contentEl?.() ?? scrollEl.value.firstElementChild
        if (wrapper) {
            contentResizeObserver = new ResizeObserver(autoFollow)
            contentResizeObserver.observe(wrapper)
        }

        if (!isSendInFlight) {
            tryInitialScroll()
        }
    })

    onBeforeUnmount(() => {
        scrollEl.value?.removeEventListener('wheel', onWheel)
        scrollEl.value?.removeEventListener('touchstart', onTouchStart)
        scrollEl.value?.removeEventListener('touchmove', onTouchMove)
        contentResizeObserver?.disconnect()
        contentResizeObserver = null
        stopMutationScroll()
        cleanupInitialObservers()
    })

    return {
        handleScroll,
        scrollToBottom,
        userScrolledUp,
        isAtBottom,
    }
}
